import React, { useState } from 'react';
import { Settings as SettingsIcon, FileType, Users, FileText } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { DeliveryNoteSettings } from './DeliveryNoteSettings';
import { SettingsGeneral } from './SettingsGeneral';
import { UserManagement } from './UserManagement';
import { useDocumentTitle } from '../utils/useDocumentTitle';
type Tab = 'general' | 'delivery-note' | 'users' | 'terms';
export function SettingsPage() {
  useDocumentTitle('Configuración');
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [termsContent, setTermsContent] = useState(() => {
    return localStorage.getItem('criket_terms') || '1. Los pedidos deben realizarse antes de la hora de corte establecida.\n\n2. Una vez confirmado el pedido, no se permiten cambios ni cancelaciones después de 5 minutos.\n\n3. Los subsidios aplicados son responsabilidad de la empresa contratante.\n\n4. Los precios incluyen ITBIS (18%) salvo empresas exentas.\n\n5. Las reclamaciones deben realizarse dentro de las 24 horas posteriores a la entrega.\n\n6. El servicio de entrega está sujeto a disponibilidad y horarios establecidos.\n\n7. La empresa se reserva el derecho de modificar el menú sin previo aviso.\n\n8. Los datos personales serán tratados conforme a la ley de protección de datos de República Dominicana.';
  });
  const [termsSaved, setTermsSaved] = useState(false);
  const tabs = [{
    id: 'general' as Tab,
    label: 'General',
    icon: SettingsIcon
  }, {
    id: 'users' as Tab,
    label: 'Usuarios',
    icon: Users
  }, {
    id: 'delivery-note' as Tab,
    label: 'Formato Conduce',
    icon: FileType
  }, {
    id: 'terms' as Tab,
    label: 'Términos y Condiciones',
    icon: FileText
  }];
  const handleSaveTerms = () => {
    localStorage.setItem('criket_terms', termsContent);
    setTermsSaved(true);
    setTimeout(() => setTermsSaved(false), 2000);
  };
  return <div className="p-4 lg:p-10 max-w-6xl mx-auto" data-id="element-5803">
      <div className="mb-6 lg:mb-8" data-id="element-5804">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5805">
          Configuración
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5806">
          Administra la configuración general del sistema
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6" data-id="element-5807">
        <div className="border-b border-gray-200 dark:border-gray-800" data-id="element-5808">
          <nav className="flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide -mb-px" data-id="element-5809">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${isActive ? 'border-black text-black dark:border-white dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-700'}`} data-id="element-5810">
                  <Icon size={16} className="shrink-0" data-id="element-5811" />
                  <span className="hidden sm:inline" data-id="element-5812">{tab.label}</span>
                  <span className="sm:hidden" data-id="element-5813">
                    {tab.id === 'general' ? 'General' : tab.id === 'users' ? 'Usuarios' : tab.id === 'delivery-note' ? 'Conduce' : 'Términos'}
                  </span>
                </button>;
          })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div data-id="element-5814">
        {activeTab === 'general' && <SettingsGeneral data-id="element-5815" />}
        {activeTab === 'users' && <UserManagement data-id="element-5816" />}
        {activeTab === 'delivery-note' && <DeliveryNoteSettings data-id="element-5817" />}
        {activeTab === 'terms' && <div className="space-y-6" data-id="element-5818">
            <Card className="p-6" data-id="element-5819">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-5820">
                Términos y Condiciones del Servicio
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-5821">
                Estos términos se mostrarán a los empleados en la página de
                pedidos. Edítalos según las políticas de tu empresa.
              </p>

              <textarea value={termsContent} onChange={e => setTermsContent(e.target.value)} rows={16} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm leading-relaxed resize-y" placeholder="Escribe los términos y condiciones aquí..." data-id="element-5822" />

              <div className="flex items-center gap-3 mt-4" data-id="element-5823">
                <button onClick={handleSaveTerms} className="px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-black rounded-lg font-medium text-sm hover:bg-black dark:hover:bg-gray-200 transition-colors" data-id="element-5824">
                  Guardar Términos
                </button>
                {termsSaved && <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium" data-id="element-5825">
                    ✓ Guardado correctamente
                  </span>}
              </div>
            </Card>

            {/* Preview */}
            <Card className="p-6" data-id="element-5826">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-5827">
                Vista Previa
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700" data-id="element-5828">
                <h4 className="text-base font-bold text-gray-900 dark:text-white mb-3" data-id="element-5829">
                  Términos y Condiciones
                </h4>
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed" data-id="element-5830">
                  {termsContent || 'No hay términos configurados.'}
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </div>;
}